import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOutletContext } from 'react-router-dom';
import { useData } from '../../context/DataContext';
import { useAuth } from '../../context/AuthContext';
import { translateLocation } from '../../utils/translationUtils';
import Modal from '../../components/Modal';
import './Departments.css';

const Departments = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { selectedLocation } = useOutletContext();
  const { data, addDepartment, updateDepartment, deleteDepartment } = useData();
  
  const [showCreateDept, setShowCreateDept] = useState(false);
  const [editingDept, setEditingDept] = useState(null);
  const [newDept, setNewDept] = useState({
    name: '',
    description: '',
    location: ''
  });

  // Filter departments by location
  const getDepartments = () => {
    let depts = data.departments || [];
    
    // If not admin, filter by user's location
    if (user.role !== 'admin' && user.location) {
      depts = depts.filter(d => d.location === user.location);
    }
    
    // If admin and location selected, filter by location
    if (user.role === 'admin' && selectedLocation && selectedLocation !== 'all') {
      depts = depts.filter(d => d.location === selectedLocation);
    }
    
    return depts;
  };

  const departments = getDepartments();

  const canDelete = (dept) => {
    // Only admin can delete departments
    return user.role === 'admin';
  };

  const handleCreateDept = (e) => {
    e.preventDefault();
    if (newDept.name) {
      const deptLocation = user.role === 'admin' 
        ? (newDept.location || selectedLocation)
        : user.location;
      
      addDepartment({
        ...newDept,
        location: deptLocation
      });
      setNewDept({ name: '', description: '', location: '' });
      setShowCreateDept(false);
    }
  };

  const handleUpdateDept = (e) => {
    e.preventDefault();
    if (editingDept) {
      updateDepartment(editingDept.id, editingDept);
      setEditingDept(null);
    }
  };

  const handleDeleteDept = (deptId) => {
    if (window.confirm(t('common.confirm') + '?')) {
      deleteDepartment(deptId);
    }
  };

  return (
    <div className="departments-page">
      <div className="page-header">
        <h1>{t('department.title')}</h1>
        <button 
          className="btn-primary" 
          onClick={() => setShowCreateDept(!showCreateDept)}
        >
          {t('department.add')}
        </button>
      </div>

      {/* Create Department Form */}
      <Modal isOpen={showCreateDept} onClose={() => setShowCreateDept(false)} title={t('department.add')}>
          <form onSubmit={handleCreateDept}>
            <div className="form-row">
              <div className="form-group">
                <label>{t('department.name')}</label>
                <input 
                  type="text" 
                  value={newDept.name}
                  onChange={(e) => setNewDept({...newDept, name: e.target.value})}
                  required
                />
              </div>
              {user.role === 'admin' && (
                <div className="form-group">
                  <label>{t('location.title')}</label>
                  <select 
                    value={newDept.location}
                    onChange={(e) => setNewDept({...newDept, location: e.target.value})}
                    required
                  >
                    <option value="">{t('common.select')}...</option>
                    {data.locations.map(l => (
                      <option key={l.id} value={l.id}>{translateLocation(t, l.id)}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
            <div className="form-group">
              <label>{t('department.description')}</label>
              <textarea 
                value={newDept.description}
                onChange={(e) => setNewDept({...newDept, description: e.target.value})}
                rows="3"
              />
            </div>
            <div className="form-actions">
              <button type="submit" className="btn-primary">{t('common.save')}</button>
              <button 
                type="button" 
                className="btn-secondary"
                onClick={() => setShowCreateDept(false)}
              >
                {t('common.cancel')}
              </button>
            </div>
          </form>
      </Modal>

      {/* Edit Department Form */}
      <Modal isOpen={!!editingDept} onClose={() => setEditingDept(null)} title={t('department.edit')}>
        {editingDept && (
          <form onSubmit={handleUpdateDept}>
            <div className="form-group">
              <label>{t('department.name')}</label>
              <input 
                type="text" 
                value={editingDept.name}
                onChange={(e) => setEditingDept({...editingDept, name: e.target.value})}
                required
              />
            </div>
            <div className="form-group">
              <label>{t('department.description')}</label>
              <textarea 
                value={editingDept.description}
                onChange={(e) => setEditingDept({...editingDept, description: e.target.value})}
                rows="3"
              />
            </div>
            <div className="form-actions">
              <button type="submit" className="btn-primary">{t('common.save')}</button>
              <button 
                type="button" 
                className="btn-secondary"
                onClick={() => setEditingDept(null)}
              >
                {t('common.cancel')}
              </button>
            </div>
          </form>
        )}
      </Modal>

      {/* Departments Grid */}
      <div className="departments-grid">
        {departments.length === 0 ? (
          <div className="no-departments">
            <p>{t('department.noDepartments')}</p>
          </div>
        ) : (
          departments.map(dept => (
            <div key={dept.id} className="department-card">
              <div className="dept-header">
                <div className="dept-icon">🏢</div>
                <div className="dept-info">
                  <h3>{dept.name}</h3>
                  <span className="dept-location">{translateLocation(t, dept.location)}</span>
                </div>
              </div>
              <p className="dept-description">{dept.description}</p>
              <div className="dept-actions">
                <button 
                  className="edit-btn"
                  onClick={() => setEditingDept(dept)}
                >
                  ✏️ {t('common.edit')}
                </button>
                {canDelete(dept) && (
                  <button 
                    className="delete-btn"
                    onClick={() => handleDeleteDept(dept.id)}
                  >
                    🗑️ {t('common.delete')}
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default Departments;
